import React, { useState } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { OnboardingStep } from './OnboardingStep';
import { OnboardingStepProps } from './OnboardingStep';

const INDUSTRY_OPTIONS = [
  {
    id: 'GENERAL',
    name: 'General Business',
    description: 'Standard business operations across various industries',
    icon: '🏢',
  },
  {
    id: 'HEALTHCARE',
    name: 'Healthcare',
    description: 'Healthcare providers, hospitals, clinics, and medical organizations',
    icon: '🏥',
  },
  {
    id: 'MANUFACTURING',
    name: 'Manufacturing',
    description: 'Manufacturing companies, production facilities, and industrial operations',
    icon: '🏭',
  },
  {
    id: 'FINANCE',
    name: 'Financial Services',
    description: 'Banks, insurance companies, investment firms, and financial institutions',
    icon: '🏦',
  },
];

export const IndustrySelectionStep: React.FC<OnboardingStepProps> = ({
  data,
  onNext,
  onPrevious,
}) => {
  const { t } = useLanguage();
  const [selectedIndustry, setSelectedIndustry] = useState(data.industryType);

  const handleIndustrySelect = (industryId: string) => {
    setSelectedIndustry(industryId);
  };

  const handleNext = () => {
    if (selectedIndustry) {
      onNext({ industryType: selectedIndustry });
    }
  };

  const canGoNext = selectedIndustry !== '';

  return (
    <OnboardingStep
      title={t('onboarding.industry.title')}
      description={t('onboarding.industry.description')}
      onNext={handleNext}
      onPrevious={onPrevious}
      canGoNext={canGoNext}
      showNext={true}
      showPrevious={false}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {INDUSTRY_OPTIONS.map((industry) => (
          <div
            key={industry.id}
            className={`relative rounded-lg border-2 p-6 cursor-pointer transition-all duration-200 ${
              selectedIndustry === industry.id
                ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-500 ring-opacity-50'
                : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
            }`}
            onClick={() => handleIndustrySelect(industry.id)}
          >
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className="text-3xl">{industry.icon}</div>
              </div>
              <div className="ml-4 flex-1">
                <h3 className="text-lg font-medium text-gray-900">
                  {industry.name}
                </h3>
                <p className="mt-2 text-sm text-gray-600">
                  {industry.description}
                </p>
              </div>
              <div className="flex-shrink-0">
                {selectedIndustry === industry.id && (
                  <div className="flex items-center justify-center w-6 h-6 bg-blue-600 rounded-full">
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {selectedIndustry && (
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                {t('onboarding.industry.selected')}
              </h3>
              <div className="mt-2 text-sm text-blue-700">
                {t('onboarding.industry.nextStep')}
              </div>
            </div>
          </div>
        </div>
      )}
    </OnboardingStep>
  );
};
